/**
 * Validate critical query inputs before Supabase calls.
 * Catches undefined/null where not allowed, invalid limits, bad filters.
 */

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export function validateNonEmptyString(value: unknown, fieldName: string): ValidationResult {
  if (value == null) {
    return { valid: false, error: `${fieldName} is required (got null/undefined)` };
  }
  const s = String(value).trim();
  if (s.length === 0) {
    return { valid: false, error: `${fieldName} cannot be empty` };
  }
  return { valid: true };
}

export function validateId(value: unknown, fieldName: string): ValidationResult {
  const r = validateNonEmptyString(value, fieldName);
  if (!r.valid) return r;
  const s = String(value).trim();
  if (s.length > 200) {
    return { valid: false, error: `${fieldName} too long` };
  }
  return { valid: true };
}

export function validateLimit(value: unknown, max = 1000): ValidationResult {
  if (value == null) return { valid: true };
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return { valid: false, error: `limit must be a non-negative number (got ${value})` };
  }
  if (n > max) {
    return { valid: false, error: `limit must be <= ${max} (got ${n})` };
  }
  return { valid: true };
}

export function validateOffset(value: unknown): ValidationResult {
  if (value == null) return { valid: true };
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) {
    return { valid: false, error: `offset must be a non-negative number (got ${value})` };
  }
  return { valid: true };
}

export function validateLibrarySort(sort: unknown): ValidationResult {
  const valid = ["recent", "alphabetical", "language", "tag", "type"];
  if (sort == null || sort === "") return { valid: true };
  if (typeof sort !== "string" || !valid.includes(sort)) {
    return { valid: false, error: `sort must be one of ${valid.join(", ")} (got ${sort})` };
  }
  return { valid: true };
}

export function validateLibraryParams(params: {
  userId?: unknown;
  limit?: unknown;
  offset?: unknown;
  sort?: unknown;
}): ValidationResult {
  if (params.userId != null) {
    const r = validateNonEmptyString(params.userId, "userId");
    if (!r.valid) return r;
  }
  const limitR = validateLimit(params.limit, 200);
  if (!limitR.valid) return limitR;
  const offsetR = validateOffset(params.offset);
  if (!offsetR.valid) return offsetR;
  const sortR = validateLibrarySort(params.sort);
  if (!sortR.valid) return sortR;
  return { valid: true };
}
